import { isWithinFiscalCapacity } from "../fiscal/taxIncentiveCalculator";
import { JOINT_MAX_SIGNERS, JOINT_MIN_SIGNERS, SIGNATURE_REGIMES } from "../investor";
import {
  createOptionalRepresentativeSchema,
  createRepresentativeSchema,
  createRepresentativeValidationMessages,
  type RepresentativeValidationMessages,
} from "./representativeSchemas";
import type { Translator } from "./validationHelpers";
import * as yup from "yup";

/**
 * ---------------------------------------------------------------------------
 * Shared field builders (DRY)
 * ---------------------------------------------------------------------------
 */

interface InvestorValidationMessages {
  required: string;
  emailInvalid: string;
  nationalIdInvalid: string;
  capacityExceeded: string;
  jointMinSigners: string;
  jointMaxSigners: string;
  jointSignerEmailRequired: string;
  representative: RepresentativeValidationMessages;
}

const INVESTOR_TYPE_VALUES = ["individual", "company"] as const;
const LANGUAGE_VALUES = ["es", "en", "ca"] as const;
const CULTURAL_ACTIVITY_VALUES = [
  "film",
  "documentary",
  "series",
  "theatre",
  "dance",
  "music_festival",
  "performing_arts",
  "audiovisual_production",
] as const;

function requiredString(msg: string) {
  return yup.string().required(msg).trim().min(1, msg);
}

function optionalString() {
  return yup.string().defined().default("");
}

function emailField(msg: string) {
  return yup.string().email(msg).defined().default("");
}

function culturalInterestsField(requiredMsg: string) {
  return yup
    .array()
    .of(
      yup
        .string()
        .oneOf([...CULTURAL_ACTIVITY_VALUES])
        .required(requiredMsg),
    )
    .defined()
    .default([]);
}

function signatureRegimeField() {
  return yup
    .string()
    .oneOf([...SIGNATURE_REGIMES])
    .defined()
    .default("sole");
}

function legalRepresentativeShape(msgs: InvestorValidationMessages) {
  return {
    uuid: optionalString(),
    first_name: requiredString(msgs.required),
    last_name: requiredString(msgs.required),
    dni: yup
      .string()
      .required(msgs.required)
      .trim()
      .min(1, msgs.required)
      .matches(/^[A-Za-z0-9-]+$/, msgs.nationalIdInvalid),
    position: optionalString(),
    phone: optionalString(),
    email: emailField(msgs.emailInvalid),
    is_primary: yup.boolean().defined().default(false),
    is_signer: yup.boolean().defined().default(true),
  };
}

interface LegalRepresentativeValue {
  email: string;
  is_signer: boolean;
}

/**
 * A joint party is dispatched to the signature provider as one submitter per
 * signing representative, so the backend rejects anything under two signers
 * and any signer without its own mailbox. Both rules are enforced here so the
 * user sees them before the contract is sent rather than at dispatch time.
 */
function legalRepresentativesField(msgs: InvestorValidationMessages, regimeKey: string) {
  const signersOf = (value: unknown) =>
    ((value ?? []) as LegalRepresentativeValue[]).filter((rep) => rep.is_signer);

  return yup
    .array()
    .of(yup.object(legalRepresentativeShape(msgs)))
    .defined()
    .default([])
    .test("joint-min-signers", msgs.jointMinSigners, function (value) {
      if (this.parent[regimeKey] !== "joint") return true;
      return signersOf(value).length >= JOINT_MIN_SIGNERS;
    })
    .test("joint-max-signers", msgs.jointMaxSigners, function (value) {
      if (this.parent[regimeKey] !== "joint") return true;
      return signersOf(value).length <= JOINT_MAX_SIGNERS;
    })
    .test("joint-signer-email", msgs.jointSignerEmailRequired, function (value) {
      if (this.parent[regimeKey] !== "joint") return true;
      return signersOf(value).every((rep) => !!rep.email?.trim());
    });
}

/**
 * ---------------------------------------------------------------------------
 * Wizard schema (4 steps, conditional fields)
 * ---------------------------------------------------------------------------
 */

export function createInvestorWizardSchema(msgs: InvestorValidationMessages) {
  return yup.object({
    // Step 1: Investor type
    type: yup
      .string()
      .oneOf([...INVESTOR_TYPE_VALUES], msgs.required)
      .required(msgs.required),

    // Step 2: Basic info — Individual
    firstName: yup.string().when("type", {
      is: "individual",
      then: (s) => s.required(msgs.required).trim().min(1, msgs.required),
      otherwise: (s) => s.defined().default(""),
    }),
    lastName: yup.string().when("type", {
      is: "individual",
      then: (s) => s.required(msgs.required).trim().min(1, msgs.required),
      otherwise: (s) => s.defined().default(""),
    }),
    dni: yup.string().when("type", {
      is: "individual",
      then: (s) =>
        s
          .required(msgs.required)
          .trim()
          .min(1, msgs.required)
          .matches(/^[A-Za-z0-9-]+$/, msgs.nationalIdInvalid),
      otherwise: (s) => s.defined().default(""),
    }),
    birthDate: optionalString(),
    profession: optionalString(),

    // Step 2: Basic info — Company
    companyName: yup.string().when("type", {
      is: "company",
      then: (s) => s.required(msgs.required).trim().min(1, msgs.required),
      otherwise: (s) => s.defined().default(""),
    }),
    cif: yup.string().when("type", {
      is: "company",
      then: (s) => s.required(msgs.required).trim().min(1, msgs.required),
      otherwise: (s) => s.defined().default(""),
    }),
    website: optionalString(),
    signatureRegime: signatureRegimeField(),
    representative: createRepresentativeSchema(msgs.representative).when("type", {
      is: "individual",
      then: () => createOptionalRepresentativeSchema(),
    }),

    // Step 3: Contact info
    email: yup.string().required(msgs.required).email(msgs.emailInvalid),
    phone: optionalString(),
    fiscalAddress: requiredString(msgs.required),
    city: requiredString(msgs.required),
    postalcode: requiredString(msgs.required),
    country: requiredString(msgs.required),
    language: yup
      .string()
      .oneOf([...LANGUAGE_VALUES], msgs.required)
      .required(msgs.required),

    // Step 4: Additional info
    investmentInterests: culturalInterestsField(msgs.required),
    taxableIncomeBase: optionalString(),
    potentialInvestmentAmount: yup
      .string()
      .defined()
      .default("")
      .test("capacity-limit", msgs.capacityExceeded, function (value) {
        return isWithinFiscalCapacity(value, this.parent.taxableIncomeBase);
      }),
  });
}

type InvestorWizardSchema = ReturnType<typeof createInvestorWizardSchema>;

/**
 * The wizard schema uses `.default()` and `.when()`, so yup's input type (keys
 * present, values may be `undefined`) differs from its output/cast type (some
 * keys optional). react-hook-form types the form against the input type and the
 * resolver's transformed result against the output type, so both are exported
 * and threaded through `useForm`'s three generics to avoid any cast.
 */
type InvestorWizardSchemaInput =
  InvestorWizardSchema extends yup.ObjectSchema<infer TInput, infer _C, infer _D, infer _F>
    ? TInput
    : never;
export type InvestorWizardFormData = Omit<InvestorWizardSchemaInput, "type"> & {
  type: InvestorWizardSchemaInput["type"] | "";
};
export type InvestorWizardFormValues = yup.InferType<InvestorWizardSchema>;

/**
 * ---------------------------------------------------------------------------
 * Edit schemas (individual + company)
 * ---------------------------------------------------------------------------
 */

export function createIndividualEditSchema(msgs: InvestorValidationMessages) {
  return yup.object({
    first_name: optionalString(),
    last_name: optionalString(),
    national_id: optionalString(),
    birth_date: optionalString(),
    profession: optionalString(),
    email: emailField(msgs.emailInvalid),
    phone: optionalString(),
    address: optionalString(),
    city: optionalString(),
    postal_code: optionalString(),
    country: optionalString(),
    communication_language: yup
      .string()
      .oneOf([...LANGUAGE_VALUES])
      .defined()
      .default("es"),
    taxable_income_base: optionalString(),
    potential_investment_amount: yup
      .string()
      .defined()
      .default("")
      .test("capacity-limit", msgs.capacityExceeded, function (value) {
        return isWithinFiscalCapacity(value, this.parent.taxable_income_base);
      }),
    cultural_interests: culturalInterestsField(msgs.required),
  });
}

export type IndividualEditFormData = yup.InferType<ReturnType<typeof createIndividualEditSchema>>;

export function createCompanyEditSchema(msgs: InvestorValidationMessages) {
  return yup.object({
    company_name: optionalString(),
    tax_id: optionalString(),
    legal_rep_first_name: optionalString(),
    legal_rep_last_name: optionalString(),
    legal_rep_national_id: optionalString(),
    website: optionalString(),
    signature_regime: signatureRegimeField(),
    legal_representatives: legalRepresentativesField(msgs, "signature_regime"),
    email: emailField(msgs.emailInvalid),
    phone: optionalString(),
    address: optionalString(),
    city: optionalString(),
    postal_code: optionalString(),
    country: optionalString(),
    communication_language: yup
      .string()
      .oneOf([...LANGUAGE_VALUES])
      .defined()
      .default("es"),
    taxable_income_base: optionalString(),
    potential_investment_amount: yup
      .string()
      .defined()
      .default("")
      .test("capacity-limit", msgs.capacityExceeded, function (value) {
        return isWithinFiscalCapacity(value, this.parent.taxable_income_base);
      }),
    cultural_interests: culturalInterestsField(msgs.required),
  });
}

export type CompanyEditFormData = yup.InferType<ReturnType<typeof createCompanyEditSchema>>;

/**
 * ---------------------------------------------------------------------------
 * Step-to-fields mapping for wizard validation (WZ-2)
 * ---------------------------------------------------------------------------
 */

export const INVESTOR_WIZARD_STEP_FIELDS = {
  "investor-type": ["type"],
  "basic-info": [
    "firstName",
    "lastName",
    "dni",
    "birthDate",
    "profession",
    "companyName",
    "cif",
    "website",
    "signatureRegime",
    "representative",
  ],
  "contact-info": ["email", "phone", "fiscalAddress", "city", "postalcode", "country", "language"],
  "additional-info": ["investmentInterests", "taxableIncomeBase", "potentialInvestmentAmount"],
} as const;

export const INVESTOR_WIZARD_STEP_IDS = [
  "investor-type",
  "basic-info",
  "contact-info",
  "additional-info",
] as const satisfies readonly (keyof typeof INVESTOR_WIZARD_STEP_FIELDS)[];

/**
 * ---------------------------------------------------------------------------
 * Validation messages factory (i18n)
 * ---------------------------------------------------------------------------
 */

export function createInvestorValidationMessages(t: Translator): InvestorValidationMessages {
  return {
    required: t("validation.required", { defaultValue: "Este campo es obligatorio" }),
    emailInvalid: t("validation.email_invalid", { defaultValue: "Email no válido" }),
    nationalIdInvalid: t("validation.national_id_invalid", {
      defaultValue: "Formato de DNI/NIE/CIF no válido",
    }),
    capacityExceeded: t("validation.capacity_exceeded", {
      defaultValue: "El importe no puede superar el 50% de la base imponible",
    }),
    jointMinSigners: t("validation.joint_min_signers", {
      defaultValue: "La firma mancomunada exige al menos dos representantes firmantes",
    }),
    jointMaxSigners: t("validation.joint_max_signers", {
      defaultValue: "La firma mancomunada admite como máximo dos representantes firmantes",
    }),
    jointSignerEmailRequired: t("validation.joint_signer_email_required", {
      defaultValue: "Cada representante firmante necesita su propio email",
    }),
    representative: createRepresentativeValidationMessages(t),
  };
}
